<template>
    <div v-if="show" class="modal-overlay" @keydown.esc.stop.prevent tabindex="0">
        <LoadingPage v-if="isSubmitting" />
        <div class="sample-contract-modal proceed-modal-custom" :class="{ 'modal-loading-active': isSubmitting }">
            <div class="modal-header-custom">
                <h5 class="modal-title-custom">Proceed to Contract</h5>
                <button @click="close" class="close-button-custom">&times;</button>
            </div>

            <div class="step-indicator">
                <div class="step" :class="{ 'active': currentStep >= 1, 'completed': currentStep > 1 }">
                    <div class="step-icon"><span v-if="currentStep > 1">✔</span><span v-else>1</span></div>
                    <div class="step-label">Confirm Products</div>
                </div>
                <div class="step-connector" :class="{ 'completed': currentStep > 1 }"></div>
                <div class="step" :class="{ 'active': currentStep >= 2, 'completed': currentStep > 2 }">
                    <div class="step-icon"><span v-if="currentStep > 2">✔</span><span v-else>2</span></div>
                    <div class="step-label">Organisation Details</div>
                </div>
                <div class="step-connector" :class="{ 'completed': currentStep > 2 }"></div>
                <div class="step" :class="{ 'active': currentStep >= 3, 'completed': currentStep > 3 }">
                    <div class="step-icon"><span v-if="currentStep > 3">✔</span><span v-else>3</span></div>
                    <div class="step-label">Contact & Signers</div>
                </div>
                <div class="step-connector" :class="{ 'completed': currentStep > 3 }"></div>
                <div class="step" :class="{ 'active': currentStep >= 4 }">
                    <div class="step-icon">4</div>
                    <div class="step-label">Finish</div>
                </div>
            </div>

            <div class="modal-body-custom">
                <div v-if="currentStep === 1">
                    <div class="items-selection-wrapper">
                        <div class="form-group" v-if="!isProposalAccepted && proposalItems.length > 1">
                            <label for="contractOption">Pricing Option</label>
                            <select id="contractOption" v-model="selectedContractOptionIndex"
                                @change="handleOptionChange" class="form-control">
                                <option v-for="pi in proposalItems" :key="pi.option_index" :value="pi.option_index">
                                    {{ (pi.option_index == 1) ? 'Main' : 'Option ' +
                                        toOptionName(pi.option_index) }}
                                </option>
                            </select>
                        </div>
                        <p>Select the items to include in the contract:</p>
                        
                        <div class="item-list" style="padding: 0;">
                            <table class="stepper-pricing-table">
                                <thead>
                                    <tr>
                                        <th class="col-select"></th>
                                        <th class="col-product">Product / Service</th>
                                        <th class="col-qty">Qty</th>
                                        <th class="col-unit-price">Price</th>
                                        <th v-if="showDiscountCalculation" class="col-discount">Per Item Discount</th>
                                        <th class="col-total">Total AUD</th>
                                    </tr>
                                </thead>
                                <tbody>
                                <!-- Previously Accepted Items -->
                                <template v-if="previouslyAcceptedItems.length > 0">
                                    <template v-for="item in previouslyAcceptedItems" :key="'accepted-' + item.id">
                                        <tr v-if="item.is_heading || item.IsHeading" class="heading-row" style="background-color: #f1f5f9;">
                                            <td :colspan="showDiscountCalculation ? 6 : 5" style="padding: 10px 16px; font-weight: bold; color: #1e293b; border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; text-align: left; vertical-align: middle;">
                                                {{ item.product_name || item.Product?.display_name || item.Product?.name || 'Section Heading' }}
                                                <span class="item-status-tag" style="margin-left: 8px;">(In Contract)</span>
                                            </td>
                                        </tr>
                                        <tr v-else class="item-row-disabled" :class="{'child-item-row': item.isChild}">
                                            <td class="col-select">
                                                <input v-if="!item.isChild || parseFloat(item.price) > 0" type="checkbox" checked disabled>
                                            </td>
                                            <td class="col-product" :style="item.isChild ? 'padding-left: 20px;' : ''">
                                                <div class="product-info">
                                                    <span v-if="item.isChild" class="child-indicator">
                                                        <i class="fa-solid fa-play" style="color: #00a0c1; font-size: 0.65em; margin-right: 6px;"></i>
                                                    </span>
                                                    <strong v-if="!item.isChild">{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</strong>
                                                    <span v-else class="child-product-title">{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</span>
                                                    <span v-if="!item.isChild" class="item-status-tag">(In Contract)</span>
                                                </div>
                                                <div v-if="item.description" class="item-description" v-html="item.description"></div>
                                            </td>
                                            <td class="col-qty">
                                                <input v-if="!item.isChild || parseFloat(item.price) > 0" type="number" :value="item.quantity" disabled class="qty-input-field" />
                                            </td>
                                            <td class="col-unit-price">
                                                <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(item.price) }}</span>
                                            </td>
                                            <td v-if="showDiscountCalculation" class="col-discount">
                                                <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(item.discount) }}</span>
                                            </td>
                                            <td class="col-total">
                                                <span v-if="item.isChild && parseFloat(item.price) <= 0" class="badge-included">Included</span>
                                                <template v-else>
                                                    <div v-if="item.discount > 0 && showDiscountCalculation" style="line-height: 1.3;">
                                                        <span style="font-size: 0.85em; color: #28a745; font-weight: 500; display: block;">
                                                            (Discount: {{ formatCurrency(item.discount * item.quantity) }})
                                                        </span>
                                                        <span>{{ formatCurrency((item.price - item.discount) * item.quantity) }}</span>
                                                    </div>
                                                    <span v-else>{{ formatCurrency((item.price - item.discount) * item.quantity) }}</span>
                                                </template>
                                            </td>
                                        </tr>
                                    </template>
                                    <tr v-if="availableItems.length > 0" class="separator-row">
                                        <td :colspan="showDiscountCalculation ? 6 : 5"><hr class="item-list-separator" style="margin: 0;" /></td>
                                    </tr>
                                </template>

                                <!-- Select All Row -->
                                <tr v-if="availableItems.length > 0" class="select-all-row">
                                    <td class="col-select">
                                        <input type="checkbox" id="selectAllCheckbox" v-model="selectAllComputed">
                                    </td>
                                    <td :colspan="showDiscountCalculation ? 5 : 4" class="col-product">
                                        <label for="selectAllCheckbox"><strong>Select All</strong></label>
                                    </td>
                                </tr>

                                <!-- Available Items -->
                                <template v-for="item in availableItems" :key="item.id">
                                    <tr v-if="item.is_heading || item.IsHeading" class="heading-row" style="background-color: #f1f5f9;">
                                        <td :colspan="showDiscountCalculation ? 6 : 5" style="padding: 10px 16px; font-weight: bold; color: #1e293b; border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; text-align: left; vertical-align: middle;">
                                            {{ item.product_name || item.Product?.display_name || item.Product?.name || 'Section Heading' }}
                                        </td>
                                    </tr>
                                    <tr v-else :class="{'child-item-row': item.isChild}">
                                        <td class="col-select">
                                            <input v-if="!item.isChild || parseFloat(item.price) > 0" type="checkbox" :id="'item-' + item.id" :value="item.id" v-model="selectedContractItems">
                                        </td>
                                        <td class="col-product" :style="item.isChild ? 'padding-left: 20px;' : ''">
                                            <div class="product-info">
                                                <span v-if="item.isChild" class="child-indicator">
                                                    <i class="fa-solid fa-play" style="color: #00a0c1; font-size: 0.65em; margin-right: 6px;"></i>
                                                </span>
                                                <strong v-if="!item.isChild">{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</strong>
                                                <span v-else class="child-product-title">{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</span>
                                            </div>
                                            <div v-if="item.description" class="item-description" v-html="item.description"></div>
                                        </td>
                                        <td class="col-qty">
                                            <input v-if="!item.isChild || parseFloat(item.price) > 0" type="number" v-model.number="item.quantity" min="1" class="qty-input-field" @change="validateQty(item)" @blur="validateQty(item)" />
                                        </td>
                                        <td class="col-unit-price">
                                            <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(item.price) }}</span>
                                        </td>
                                        <td v-if="showDiscountCalculation" class="col-discount">
                                            <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(item.discount) }}</span>
                                        </td>
                                        <td class="col-total">
                                            <span v-if="item.isChild && parseFloat(item.price) <= 0" class="badge-included">Included</span>
                                            <template v-else>
                                                <div v-if="item.discount > 0" style="line-height: 1.3;">
                                                    <span style="font-size: 0.85em; color: #28a745; font-weight: 500; display: block;">
                                                        (Discount: {{ formatCurrency(item.discount * item.quantity) }})
                                                    </span>
                                                    <span>{{ formatCurrency((item.price - item.discount) * item.quantity) }}</span>
                                                </div>
                                                <span v-else>{{ formatCurrency((item.price - item.discount) * item.quantity) }}</span>
                                            </template>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                            </table>
                            
                            <div v-if="currentOptionProducts.length === 0" class="no-items-message">
                                <p>There are no items in this option.</p>
                            </div>
                        </div>
                    </div>
                    <div v-if="selectedContractItems.length > 0" class="contract-summary">
                        <h4></h4>
                        <div class="summary-row" :title="subtotalTooltipText">
                            <span style="cursor: help; border-bottom: 1px dotted #999;">Subtotal</span>
                            <span>{{ formatCurrency(selectedItemsSummary.subtotal) }}</span>
                        </div>
                        <div class="summary-row"><span title="Goods and Services Tax">GST (10%)</span><span>{{
                            formatCurrency(selectedItemsSummary.gst) }}</span></div>
                        <div class="summary-row total"><strong>Total AUD</strong><strong>{{
                            formatCurrency(selectedItemsSummary.total) }}</strong></div>
                    </div>
                </div>

                <div v-if="currentStep === 2" class="contract-details-form">
                     <div class="form-row">
                        <div class="form-group half-width">
                            <label for="orgName">Organisation Name<span class="required-star">*</span></label>
                            <input type="text" id="orgName" class="form-control"
                                v-model="contractDetails.organisationName" :disabled="abnRecordFound">
                        </div>
                        <div class="form-group half-width">
                            <label for="abn">
                                {{ businessNumberLabel }}
                                <span v-if="isAustraliaResident" class="required-star">*</span>
                            </label>
                            <input type="text" id="abn" class="form-control" v-model="contractDetails.abn"
                                @keyup="handleAbnInput" :disabled="abnLookup.isLoading || this.contractAcceptances.length > 0"
                                :placeholder="isAustraliaResident ? businessNumberPlaceholder : `${businessNumberPlaceholder} (Optional)`">
                            <p v-if="abnLookup.isLoading" class="lookup-message">Validating {{ businessNumberShortLabel }}...</p>
                            <p v-if="!abnLookup.isLoading && abnLookup.message" class="lookup-message"
                                :class="abnLookup.status">{{ abnLookup.message }}</p>
                        </div>
                    </div>
                    <div v-if="abnRecordFound && organisationNameOptions.length > 1" class="form-group">
                        <label for="verifiedNameSelect">Select Verified Name</label>
                        <select id="verifiedNameSelect" class="form-control" v-model="contractDetails.organisationName">
                            <option v-for="name in organisationNameOptions" :key="name" :value="name">
                                {{ name }}
                            </option>
                        </select>
                    </div>
                    
                    <div class="form-group">
                        <label for="orgPhone">Organisation Phone Number<span class="required-star">*</span></label>
                        <vue-tel-input 
                            id="orgPhone" 
                            v-model.trim="contractDetails.phoneNumber" 
                            v-bind="vueTel.props"
                            @validate="handlePhoneValidate"
                            mode="international"
                        ></vue-tel-input>
                        
                        <p v-if="!isPhoneValid && contractDetails.phoneNumber" class="lookup-message error">
                            Please enter a valid phone number.
                        </p>
                    </div>

                    <div class="form-group">
                        <label for="orgAddress">Organisation Address<span class="required-star">*</span></label>

                        <GMapAutocomplete v-if="!manualAddress" id="orgAddress" class="form-control"
                            placeholder="Start typing your address..." @place_changed="handleAddressSelected"
                            :value="contractDetails.address">
                        </GMapAutocomplete>

                        <div v-else class="manual-address-container">
                             <div class="form-group" style="margin-bottom: 10px;">
                                <label style="font-size: 0.85em; color: #666;">Full Address Preview (Auto-generated)</label>
                                <input type="text" class="form-control" v-model="contractDetails.address" readonly style="background-color: #e9ecef; color: #555;">
                            </div>

                            <div class="form-group">
                                <label>Address Line 1<span class="required-star">*</span></label>
                                <input type="text" class="form-control" v-model="contractDetails.address1" @input="updateFullAddress" placeholder="Street address, P.O. box">
                            </div>
                            <div class="form-group">
                                <label>Address Line 2</label>
                                <input type="text" class="form-control" v-model="contractDetails.address2" @input="updateFullAddress" placeholder="Apartment, suite, unit, etc.">
                            </div>
                            <div class="form-row">
                                <div class="form-group half-width">
                                    <label>City<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.city" @input="updateFullAddress">
                                </div>
                                <div class="form-group half-width">
                                    <label>State<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.state" @input="updateFullAddress">
                                </div>
                            </div>
                            <div class="form-row">
                                <div class="form-group half-width">
                                    <label>Country<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.country" @input="updateFullAddress">
                                </div>
                                <div class="form-group half-width">
                                    <label>Post Code<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.postCode" @input="updateFullAddress">
                                </div>
                            </div>
                        </div>

                        <div class="checkbox-group"> 
                            <input type="checkbox" id="manualAddressCheckbox" v-model="manualAddress" />
                            <label for="manualAddressCheckbox">
                                Can't find address? Enter manually
                            </label>
                        </div>
                    </div>

                    <div class="checkbox-group">
                        <input type="checkbox" id="sameAddress" v-model="contractDetails.isSameAsOrgAddress">
                        <label for="sameAddress">Address for Service of Notices is same as Organisation Address</label>
                    </div>

                    <div v-if="!contractDetails.isSameAsOrgAddress" class="form-group">
                        <label for="serviceAddress">Address for Service of Notices<span class="required-star">*</span></label>

                        <GMapAutocomplete v-if="!manualServiceAddress" id="serviceAddress" class="form-control"
                            placeholder="Start typing service address..." @place_changed="handleServiceAddressSelected"
                            :value="contractDetails.serviceAddress">
                        </GMapAutocomplete>

                        <div v-else class="manual-address-container">
                            <div class="form-group" style="margin-bottom: 10px;">
                                <label style="font-size: 0.85em; color: #666;">Full Address Preview (Auto-generated)</label>
                                <input type="text" class="form-control" v-model="contractDetails.serviceAddress" readonly style="background-color: #e9ecef; color: #555;">
                            </div>

                            <div class="form-group">
                                <label>Address Line 1<span class="required-star">*</span></label>
                                <input type="text" class="form-control" v-model="contractDetails.serviceAddress1" @input="updateFullServiceAddress" placeholder="Street address, P.O. box">
                            </div>
                            <div class="form-group">
                                <label>Address Line 2</label>
                                <input type="text" class="form-control" v-model="contractDetails.serviceAddress2" @input="updateFullServiceAddress" placeholder="Apartment, suite, unit, etc.">
                            </div>
                            <div class="form-row">
                                <div class="form-group half-width">
                                    <label>City<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.serviceCity" @input="updateFullServiceAddress">
                                </div>
                                <div class="form-group half-width">
                                    <label>State<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.serviceState" @input="updateFullServiceAddress">
                                </div>
                            </div>
                            <div class="form-row">
                                <div class="form-group half-width">
                                    <label>Country<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.serviceCountry" @input="updateFullServiceAddress">
                                </div>
                                <div class="form-group half-width">
                                    <label>Post Code<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.servicePostCode" @input="updateFullServiceAddress">
                                </div>
                            </div>
                        </div>

                        <div class="checkbox-group">
                            <input type="checkbox" id="manualServiceAddressCheckbox" v-model="manualServiceAddress" />
                            <label for="manualServiceAddressCheckbox">
                                Can't find address? Enter manually
                            </label>
                        </div>
                    </div>

                    <div class="checkbox-group" style="margin-top: 15px; border-top: 1px solid #eee; padding-top: 10px;">
                        <input type="checkbox" id="sameDeliveryAddress" v-model="contractDetails.isDeliverySameAsOrgAddress">
                        <label for="sameDeliveryAddress">Delivery Address is same as Organisation Address</label>
                    </div>

                    <div v-if="!contractDetails.isDeliverySameAsOrgAddress" class="form-group">
                        <label for="deliveryAddress">Delivery Address<span class="required-star">*</span></label>

                        <GMapAutocomplete v-if="!manualDeliveryAddress" id="deliveryAddress" class="form-control"
                            placeholder="Start typing delivery address..." @place_changed="handleDeliveryAddressSelected"
                            :value="contractDetails.deliveryAddress">
                        </GMapAutocomplete>

                        <div v-else class="manual-address-container">
                            <div class="form-group" style="margin-bottom: 10px;">
                                <label style="font-size: 0.85em; color: #666;">Full Address Preview (Auto-generated)</label>
                                <input type="text" class="form-control" v-model="contractDetails.deliveryAddress" readonly style="background-color: #e9ecef; color: #555;">
                            </div>

                            <div class="form-group">
                                <label>Address Line 1<span class="required-star">*</span></label>
                                <input type="text" class="form-control" v-model="contractDetails.deliveryAddress1" @input="updateFullDeliveryAddress" placeholder="Street address, P.O. box">
                            </div>
                            <div class="form-group">
                                <label>Address Line 2</label>
                                <input type="text" class="form-control" v-model="contractDetails.deliveryAddress2" @input="updateFullDeliveryAddress" placeholder="Apartment, suite, unit, etc.">
                            </div>
                            <div class="form-row">
                                <div class="form-group half-width">
                                    <label>City<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.deliveryCity" @input="updateFullDeliveryAddress">
                                </div>
                                <div class="form-group half-width">
                                    <label>State<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.deliveryState" @input="updateFullDeliveryAddress">
                                </div>
                            </div>
                            <div class="form-row">
                                <div class="form-group half-width">
                                    <label>Country<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.deliveryCountry" @input="updateFullDeliveryAddress">
                                </div>
                                <div class="form-group half-width">
                                    <label>Post Code<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.deliveryPostCode" @input="updateFullDeliveryAddress">
                                </div>
                            </div>
                        </div>

                        <div class="checkbox-group">
                            <input type="checkbox" id="manualDeliveryAddressCheckbox" v-model="manualDeliveryAddress" />
                            <label for="manualDeliveryAddressCheckbox">
                                Can't find address? Enter manually
                            </label>
                        </div>
                    </div>

                    <div class="checkbox-group" style="margin-top: 15px; border-top: 1px solid #eee; padding-top: 10px;">
                        <input type="checkbox" id="sameBillingAddress" v-model="contractDetails.isBillingSameAsOrgAddress">
                        <label for="sameBillingAddress">Billing Address is same as Organisation Address</label>
                    </div>

                    <div v-if="!contractDetails.isBillingSameAsOrgAddress" class="form-group">
                        <label for="billingAddress">Billing Address<span class="required-star">*</span></label>

                        <GMapAutocomplete v-if="!manualBillingAddress" id="billingAddress" class="form-control"
                            placeholder="Start typing billing address..." @place_changed="handleBillingAddressSelected"
                            :value="contractDetails.billingAddress">
                        </GMapAutocomplete>

                        <div v-else class="manual-address-container">
                            <div class="form-group" style="margin-bottom: 10px;">
                                <label style="font-size: 0.85em; color: #666;">Full Address Preview (Auto-generated)</label>
                                <input type="text" class="form-control" v-model="contractDetails.billingAddress" readonly style="background-color: #e9ecef; color: #555;">
                            </div>

                            <div class="form-group">
                                <label>Address Line 1<span class="required-star">*</span></label>
                                <input type="text" class="form-control" v-model="contractDetails.billingAddress1" @input="updateFullBillingAddress" placeholder="Street address, P.O. box">
                            </div>
                            <div class="form-group">
                                <label>Address Line 2</label>
                                <input type="text" class="form-control" v-model="contractDetails.billingAddress2" @input="updateFullBillingAddress" placeholder="Apartment, suite, unit, etc.">
                            </div>
                            <div class="form-row">
                                <div class="form-group half-width">
                                    <label>City<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.billingCity" @input="updateFullBillingAddress">
                                </div>
                                <div class="form-group half-width">
                                    <label>State<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.billingState" @input="updateFullBillingAddress">
                                </div>
                            </div>
                            <div class="form-row">
                                <div class="form-group half-width">
                                    <label>Country<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.billingCountry" @input="updateFullBillingAddress">
                                </div>
                                <div class="form-group half-width">
                                    <label>Post Code<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="contractDetails.billingPostCode" @input="updateFullBillingAddress">
                                </div>
                            </div>
                        </div>

                        <div class="checkbox-group">
                            <input type="checkbox" id="manualBillingAddressCheckbox" v-model="manualBillingAddress" />
                            <label for="manualBillingAddressCheckbox">
                                Can't find address? Enter manually
                            </label>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-group half-width">
                            <label for="serviceEmail">Email address for Service of Notices<span class="required-star">*</span></label>
                            <input type="email" id="serviceEmail" class="form-control" v-model="contractDetails.serviceEmail" @blur="validateEmail('serviceEmail')">
                            <p v-if="emailErrors.serviceEmail" class="lookup-message error">{{ emailErrors.serviceEmail }}</p>
                        </div>
                        <div class="form-group half-width">
                            <label for="accountsEmail">Email address for Accounts Team<span class="required-star">*</span></label>
                            <input type="email" id="accountsEmail" class="form-control" v-model="contractDetails.accountsEmail" @blur="validateEmail('accountsEmail')">
                            <p v-if="emailErrors.accountsEmail" class="lookup-message error">{{ emailErrors.accountsEmail }}</p>
                        </div>
                    </div>
                </div>

                <div v-if="currentStep === 3" class="signers-form">
                    
                    <div class="section-title">Primary Contact</div>
                    <div class="signer-entry primary-contact-box">
                        <div class="form-row">
                            <div class="form-group half-width">
                                <label>First Name<span class="required-star">*</span></label>
                                <input type="text" class="form-control" v-model="primaryContact.firstName" @input="syncPrimaryContactName" placeholder="e.g. John">
                            </div>
                            <div class="form-group half-width">
                                <label>Last Name<span class="required-star">*</span></label>
                                <input type="text" class="form-control" v-model="primaryContact.lastName" @input="syncPrimaryContactName" placeholder="e.g. Doe">
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group half-width">
                                <label>Email<span class="required-star">*</span></label>
                                <input type="email" class="form-control" v-model="primaryContact.email" placeholder="e.g. john@company.com">
                            </div>
                            <div class="form-group half-width">
                                <label>Phone Number<span class="required-star">*</span></label>
                                <vue-tel-input 
                                    v-model.trim="primaryContact.phone" 
                                    v-bind="vueTel.props"
                                    @validate="handleContactPhoneValidate"
                                    mode="international"
                                ></vue-tel-input>
                                <p v-if="!isContactPhoneValid && primaryContact.phone" class="lookup-message error">
                                    Please enter a valid phone number.
                                </p>
                            </div>
                        </div>

                        <div class="is-signer-toggle">
                            <span>Is this person <strong>one of the signatories</strong> for the contract?</span>
                            <div class="checkbox-group">
                                <input type="checkbox" id="primaryIsSigner" v-model="primaryContact.isSigner">
                                <label for="primaryIsSigner">Yes</label>
                            </div>
                        </div>
                    </div>

                    <hr>

                    <div class="step-header">
                        <div class="section-title" style="margin-bottom:0; border:none; padding:0;">Contract Signatories</div>
                        <button class="button-add-signer" @click="addSigner">
                            <span class="plus-icon">+</span> Add Another Signer
                        </button>
                    </div>

                    <div class="signers-list">
                        <div v-for="(signer, index) in signers" :key="index" class="signer-entry" 
                            :class="{ 'disabled-entry': signer.isPrimaryContact }">
                            
                            <div class="signer-entry-header">
                                <div class="signer-sequence-info">
                                    <span class="signer-role-badge" :class="index === 0 ? 'primary-badge' : 'regular-badge'">
                                        {{ index === 0 ? 'Primary Signatory' : 'Signatory' }}
                                    </span>
                                    <span class="sequence-number-label" v-if="allToSign">
                                        &bull; Sequence Position: <strong>{{ index + 1 }}</strong>
                                    </span>
                                    <span class="linked-badge-text" v-if="signer.isPrimaryContact">
                                        &bull; Linked to Primary Contact
                                    </span>
                                </div>
                                <div class="signer-actions-box">
                                    <div class="reorder-controls" v-if="allToSign && signers.length > 1">
                                        <button class="reorder-btn up-btn" @click.prevent="moveSignerUp(index)" :disabled="index === 0" title="Move Up">▲</button>
                                        <button class="reorder-btn down-btn" @click.prevent="moveSignerDown(index)" :disabled="index === signers.length - 1" title="Move Down">▼</button>
                                    </div>
                                    <button v-if="allToSign && (!signer.isPrimaryContact || index > 0)" 
                                        @click="removeSigner(index)" class="remove-signer-btn" title="Remove Signer">&times;</button>
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-group half-width" style="margin-bottom: 0;">
                                    <label>First Name<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="signer.firstName" 
                                        @input="syncSignerName(signer)"
                                        :disabled="signer.isPrimaryContact" placeholder="e.g. John">
                                </div>
                                <div class="form-group half-width" style="margin-bottom: 0;">
                                    <label>Last Name<span class="required-star">*</span></label>
                                    <input type="text" class="form-control" v-model="signer.lastName" 
                                        @input="syncSignerName(signer)"
                                        :disabled="signer.isPrimaryContact" placeholder="e.g. Doe">
                                </div>
                            </div>
                            <div class="form-row" style="margin-top: 12px;">
                                <div class="form-group" style="flex: 1; margin-bottom: 0;">
                                    <label>Signer Email<span class="required-star">*</span></label>
                                    <input type="email" class="form-control" :class="{ 'input-error': isDuplicateSignerEmail(signer.email, index) }" v-model="signer.email" 
                                        :disabled="signer.isPrimaryContact" placeholder="e.g. john@company.com">
                                    <p v-if="isDuplicateSignerEmail(signer.email, index)" class="lookup-message error" style="margin-top: 4px; font-size: 12px;">
                                        This email address is already added as a signer.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <p v-if="!isPrimarySignerValid" class="error-message">Exactly one signer must be the 'Primary Signatory'.</p>
                    <p v-if="hasDuplicateSignerEmails" class="error-message">Duplicate signer email detected. Each signer must have a unique email address.</p>
                    <p v-if="!isSignerDetailsValid && isPrimarySignerValid && !hasDuplicateSignerEmails" class="error-message">All signer first names, last names, and valid emails are required.</p>
                </div>

                <div v-if="currentStep === 4" class="finish-step">
                    <div class="finish-icon">🎉</div>
                    <h3>Contract Creation Ready</h3>
                    <p>Your contract will now be generated based on the details you provided. Click 'Submit Contract Details'
                        to finalise and proceed.</p>
                </div>
            </div>

            <div class="modal-footer-custom">
                <button v-if="currentStep > 1" class="button secondary" @click="prevStep">Back</button>
                <button v-if="currentStep === 1" class="button primary" @click="nextStep"
                    :disabled="selectedContractItems.length === 0">Confirm Products & Services</button>
                <button v-if="currentStep === 2" class="button primary" @click="nextStep"
                    :disabled="!isStep2Valid">Confirm Details</button>
                <button v-if="currentStep === 3" class="button primary" @click="nextStep"
                    :disabled="!isStep3Valid">Confirm Signers</button>
                <button v-if="currentStep === 4" class="button primary" @click="handleSubmitContract" :disabled="isSubmitting">
                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true" style="margin-right: 8px;"></span>
                    {{ isSubmitting ? 'Generating Contract...' : 'Submit Contract Details' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import LoadingPage from '@/components/Utilities/vertex-loading.vue';
import { toOptionName } from '../../../plugins/helper';
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'
import {
    normalizeCountryCode,
    isAustralianCountry,
    getBusinessNumberLabel,
    getBusinessNumberShortLabel,
    getBusinessNumberPlaceholder,
    validateBusinessNumber,
} from '@/plugins/businessValidator.js';

export default {
    name: 'ProceedToContractWizard',

    components: {
        VueTelInput,
        LoadingPage
    },
    props: {
        show: { type: Boolean, required: true },
        showDiscountCalculation: { type: Boolean, default: false },
        proposalItems: { type: Array, default: () => [] },
        lead: { type: Object, default: () => ({}) },
        sharedUsers: { type: Array, default: () => [] },
        proposalUniqueKey: { type: String, required: true },
        proposalAccessKey: { type: String, required: true },
        isProposalAccepted: { type: Boolean, default: false },
        acceptedOptionIndex: { type: Number, default: null },
        contractAcceptances: { type: Array, default: () => [] },
    },
    data() {
        return {
            currentStep: 1,
            selectedContractOptionIndex: 1,
            selectedContractItems: [],
            abnDebounceTimer: null,
            abnLookup: { isLoading: false, status: 'idle', message: '' },
            contractDetails: { 
                abn: '', 
                organisationName: '', 
                phoneNumber: '', 
                address: '', 
                address1: '',
                address2: '',
                city: '',
                state: '',
                country: '',
                postCode: '',
                
                serviceAddress: '', 
                serviceAddress1: '',
                serviceAddress2: '',
                serviceCity: '',
                serviceState: '',
                serviceCountry: '',
                servicePostCode: '',

                deliveryAddress: '', 
                deliveryAddress1: '',
                deliveryAddress2: '',
                deliveryCity: '',
                deliveryState: '',
                deliveryCountry: '',
                deliveryPostCode: '',

                billingAddress: '',
                billingAddress1: '',
                billingAddress2: '',
                billingCity: '',
                billingState: '',
                billingCountry: '',
                billingPostCode: '',
                
                isSameAsOrgAddress: true, 
                isDeliverySameAsOrgAddress: true, 
                isBillingSameAsOrgAddress: true,
                serviceEmail: '', 
                accountsEmail: '' 
            },
            primaryContact: {
                firstName: '',
                lastName: '',
                first_name: '',
                last_name: '',
                name: '',
                email: '',
                phone: '',
                isSigner: true
            },
            signers: [],
            originalOrganisationName: '',
            abnRecordFound: false,
            abnDetails: null,
            manualAddress: false,
            manualServiceAddress: false,
            manualDeliveryAddress: false,
            manualBillingAddress: false,
            isSubmitting: false,
            emailErrors: {
                serviceEmail: '',
                accountsEmail: ''
            },
            
            isPhoneValid: false,
            isContactPhoneValid: false,
            vueTel: {
                props: {
                    preferredCountries: ['AU', 'US'],
                    defaultCountry: "AU",
                    validCharactersOnly: true,
                    dropdownOptions: {
                        showDialCodeInList: true,
                        showFlags: true,
                        showSearchBox: true,
                    },
                    inputOptions: {
                        autocomplete: "on",
                        showDialCode: true,
                        placeholder: "Contact Number",
                        styleClasses: "form-control-phone",
                    },
                }
            },
        };
    },
    watch: {
        show(isNowVisible) {
            if (isNowVisible) {
                const leadData = this.lead || {};
                const orgData = leadData.organisation || leadData.Organisation || {};

                const formatCompositeAddress = (line1, line2, city, state, country, postcode) => {
                    return [line1, line2, city, state, country, postcode].filter(p => p && String(p).trim()).join(', ');
                };

                const orgAddress1 = orgData.address || '';
                const orgAddress2 = orgData.address2 || '';
                const orgCity = orgData.city || '';
                const orgState = orgData.state || '';
                const orgCountry = orgData.country || orgData.country_code || leadData.country_code || '';
                const orgPostCode = orgData.postcode || '';
                const defaultOrgAddress = formatCompositeAddress(orgAddress1, orgAddress2, orgCity, orgState, orgCountry, orgPostCode) || leadData.country_code || '';

                const serviceAddress1 = orgData.service_address || '';
                const serviceAddress2 = orgData.service_address2 || '';
                const serviceCity = orgData.service_city || '';
                const serviceState = orgData.service_state || '';
                const serviceCountry = orgData.service_country || '';
                const servicePostCode = orgData.service_postcode || '';
                const defaultServiceAddress = formatCompositeAddress(serviceAddress1, serviceAddress2, serviceCity, serviceState, serviceCountry, servicePostCode);

                const deliveryAddress1 = orgData.delivery_address || '';
                const deliveryAddress2 = orgData.delivery_address2 || '';
                const deliveryCity = orgData.delivery_city || '';
                const deliveryState = orgData.delivery_state || '';
                const deliveryCountry = orgData.delivery_country || '';
                const deliveryPostCode = orgData.delivery_postcode || '';
                const defaultDeliveryAddress = formatCompositeAddress(deliveryAddress1, deliveryAddress2, deliveryCity, deliveryState, deliveryCountry, deliveryPostCode);

                const billingAddress1 = orgData.billing_address || '';
                const billingAddress2 = orgData.billing_address2 || '';
                const billingCity = orgData.billing_city || '';
                const billingState = orgData.billing_state || '';
                const billingCountry = orgData.billing_country || '';
                const billingPostCode = orgData.billing_postcode || '';
                const defaultBillingAddress = formatCompositeAddress(billingAddress1, billingAddress2, billingCity, billingState, billingCountry, billingPostCode);

                const isSameAsOrgAddress = !defaultServiceAddress || defaultServiceAddress === defaultOrgAddress;
                const isDeliverySameAsOrgAddress = !defaultDeliveryAddress || defaultDeliveryAddress === defaultOrgAddress;
                const isBillingSameAsOrgAddress = !defaultBillingAddress || defaultBillingAddress === defaultOrgAddress;

                this.originalOrganisationName = leadData.auth_organisation_name || orgData.name || leadData.organisation_name || '';
                this.abnRecordFound = false;
                this.abnDetails = null;
                this.currentStep = 1;
                this.abnLookup = { isLoading: false, status: 'idle', message: '' };
                this.emailErrors = { serviceEmail: '', accountsEmail: '' };
                this.isPhoneValid = !!(orgData.phone_number || leadData.phone_number); 
                this.isContactPhoneValid = !!(leadData.phone_number || orgData.phone_number);
                
                if (this.isProposalAccepted) {
                    this.selectedContractOptionIndex = this.acceptedOptionIndex;
                    this.$nextTick(() => {
                        this.selectedContractItems = this.availableItems
                            .filter(item => !item.is_heading && !item.IsHeading && (!item.isChild || parseFloat(item.price) > 0)) 
                            .map(item => item.id);
                    });
                } else {
                    this.selectedContractOptionIndex = 1;
                    this.handleOptionChange();
                }

                this.contractDetails = {
                    abn: orgData.abn || orgData.business_number || leadData.abn || leadData.abn_info || leadData.business_number || '',
                    organisationName: this.originalOrganisationName,
                    phoneNumber: orgData.phone_number || leadData.phone_number || '', 
                    address: defaultOrgAddress, 
                    address1: orgAddress1, 
                    address2: orgAddress2, 
                    city: orgCity, 
                    state: orgState, 
                    country: orgCountry, 
                    postCode: orgPostCode,
                    serviceAddress: isSameAsOrgAddress ? defaultOrgAddress : defaultServiceAddress, 
                    serviceAddress1: isSameAsOrgAddress ? orgAddress1 : serviceAddress1, 
                    serviceAddress2: isSameAsOrgAddress ? orgAddress2 : serviceAddress2, 
                    serviceCity: isSameAsOrgAddress ? orgCity : serviceCity, 
                    serviceState: isSameAsOrgAddress ? orgState : serviceState, 
                    serviceCountry: isSameAsOrgAddress ? orgCountry : serviceCountry, 
                    servicePostCode: isSameAsOrgAddress ? orgPostCode : servicePostCode,
                    deliveryAddress: isDeliverySameAsOrgAddress ? defaultOrgAddress : defaultDeliveryAddress, 
                    deliveryAddress1: isDeliverySameAsOrgAddress ? orgAddress1 : deliveryAddress1, 
                    deliveryAddress2: isDeliverySameAsOrgAddress ? orgAddress2 : deliveryAddress2, 
                    deliveryCity: isDeliverySameAsOrgAddress ? orgCity : deliveryCity, 
                    deliveryState: isDeliverySameAsOrgAddress ? orgState : deliveryState, 
                    deliveryCountry: isDeliverySameAsOrgAddress ? orgCountry : deliveryCountry, 
                    deliveryPostCode: isDeliverySameAsOrgAddress ? orgPostCode : deliveryPostCode,
                    billingAddress: isBillingSameAsOrgAddress ? defaultOrgAddress : defaultBillingAddress,
                    billingAddress1: isBillingSameAsOrgAddress ? orgAddress1 : billingAddress1, 
                    billingAddress2: isBillingSameAsOrgAddress ? orgAddress2 : billingAddress2, 
                    billingCity: isBillingSameAsOrgAddress ? orgCity : billingCity, 
                    billingState: isBillingSameAsOrgAddress ? orgState : billingState, 
                    billingCountry: isBillingSameAsOrgAddress ? orgCountry : billingCountry, 
                    billingPostCode: isBillingSameAsOrgAddress ? orgPostCode : billingPostCode,
                    isSameAsOrgAddress: isSameAsOrgAddress,
                    isDeliverySameAsOrgAddress: isDeliverySameAsOrgAddress,
                    isBillingSameAsOrgAddress: isBillingSameAsOrgAddress,
                    serviceEmail: orgData.service_email || leadData.email || '',
                    accountsEmail: orgData.billing_email || ''
                };
                
                if (this.contractAcceptances && this.contractAcceptances.length > 0) {
                    // Backend orders by accepted_at DESC, so [0] is the latest acceptance
                    const latestAcceptance = this.contractAcceptances[0];
                    this.contractDetails.organisationName = latestAcceptance.organisation_name ?? this.contractDetails.organisationName;
                    this.contractDetails.abn = latestAcceptance.abn || latestAcceptance.business_number || this.contractDetails.abn;
                    this.contractDetails.phoneNumber = latestAcceptance.phone_number ?? this.contractDetails.phoneNumber;

                    // Restore organisation address (composite + granular)
                    this.contractDetails.address = latestAcceptance.address ?? this.contractDetails.address;
                    this.contractDetails.address1 = latestAcceptance.address_1 ?? this.contractDetails.address1;
                    this.contractDetails.address2 = latestAcceptance.address_2 ?? this.contractDetails.address2;
                    this.contractDetails.city = latestAcceptance.city ?? this.contractDetails.city;
                    this.contractDetails.state = latestAcceptance.state ?? this.contractDetails.state;
                    this.contractDetails.country = latestAcceptance.country || latestAcceptance.country_code || this.contractDetails.country;
                    this.contractDetails.postCode = latestAcceptance.post_code ?? this.contractDetails.postCode;

                    // Restore service address (composite + granular)
                    this.contractDetails.serviceAddress = latestAcceptance.service_address ?? this.contractDetails.serviceAddress;
                    this.contractDetails.serviceAddress1 = latestAcceptance.service_address_1 ?? this.contractDetails.serviceAddress1;
                    this.contractDetails.serviceAddress2 = latestAcceptance.service_address_2 ?? this.contractDetails.serviceAddress2;
                    this.contractDetails.serviceCity = latestAcceptance.service_city ?? this.contractDetails.serviceCity;
                    this.contractDetails.serviceState = latestAcceptance.service_state ?? this.contractDetails.serviceState;
                    this.contractDetails.serviceCountry = latestAcceptance.service_country ?? this.contractDetails.serviceCountry;
                    this.contractDetails.servicePostCode = latestAcceptance.service_post_code ?? this.contractDetails.servicePostCode;

                    // Restore delivery address (composite + granular)
                    this.contractDetails.deliveryAddress = latestAcceptance.delivery_address ?? this.contractDetails.deliveryAddress;
                    this.contractDetails.deliveryAddress1 = latestAcceptance.delivery_address_1 ?? this.contractDetails.deliveryAddress1;
                    this.contractDetails.deliveryAddress2 = latestAcceptance.delivery_address_2 ?? this.contractDetails.deliveryAddress2;
                    this.contractDetails.deliveryCity = latestAcceptance.delivery_city ?? this.contractDetails.deliveryCity;
                    this.contractDetails.deliveryState = latestAcceptance.delivery_state ?? this.contractDetails.deliveryState;
                    this.contractDetails.deliveryCountry = latestAcceptance.delivery_country ?? this.contractDetails.deliveryCountry;
                    this.contractDetails.deliveryPostCode = latestAcceptance.delivery_post_code ?? this.contractDetails.deliveryPostCode;

                    // Restore billing address (composite + granular)
                    this.contractDetails.billingAddress = latestAcceptance.billing_address ?? this.contractDetails.billingAddress;
                    this.contractDetails.billingAddress1 = latestAcceptance.billing_address_1 ?? this.contractDetails.billingAddress1;
                    this.contractDetails.billingAddress2 = latestAcceptance.billing_address_2 ?? this.contractDetails.billingAddress2;
                    this.contractDetails.billingCity = latestAcceptance.billing_city ?? this.contractDetails.billingCity;
                    this.contractDetails.billingState = latestAcceptance.billing_state ?? this.contractDetails.billingState;
                    this.contractDetails.billingCountry = latestAcceptance.billing_country ?? this.contractDetails.billingCountry;
                    this.contractDetails.billingPostCode = latestAcceptance.billing_post_code ?? this.contractDetails.billingPostCode;

                    this.contractDetails.serviceEmail = latestAcceptance.service_email ?? this.contractDetails.serviceEmail;
                    this.contractDetails.accountsEmail = latestAcceptance.accounts_email ?? this.contractDetails.accountsEmail;
                    this.contractDetails.isSameAsOrgAddress = !!(this.contractDetails.address && (!this.contractDetails.serviceAddress || this.contractDetails.address === this.contractDetails.serviceAddress));
                    this.contractDetails.isDeliverySameAsOrgAddress = !!(this.contractDetails.address && (!this.contractDetails.deliveryAddress || this.contractDetails.address === this.contractDetails.deliveryAddress));
                    this.contractDetails.isBillingSameAsOrgAddress = !!(this.contractDetails.address && (!this.contractDetails.billingAddress || this.contractDetails.address === this.contractDetails.billingAddress));
                }

                const splitFullName = (fullName) => {
                    const parts = (fullName || '').trim().split(/\s+/);
                    const firstName = parts[0] || '';
                    const lastName = parts.slice(1).join(' ');
                    return { firstName, lastName };
                };

                let pcFirstName = '';
                let pcLastName = '';
                let pcEmail = '';
                let pcPhone = '';

                if (this.contractAcceptances && this.contractAcceptances.length > 0) {
                    const latestAcceptance = this.contractAcceptances[0];
                    pcFirstName = latestAcceptance.primary_contact_first_name || '';
                    pcLastName = latestAcceptance.primary_contact_last_name || '';
                    if (!pcFirstName && !pcLastName && latestAcceptance.primary_contact_name) {
                        const parsed = splitFullName(latestAcceptance.primary_contact_name);
                        pcFirstName = parsed.firstName;
                        pcLastName = parsed.lastName;
                    }
                    pcEmail = latestAcceptance.primary_contact_email || '';
                    pcPhone = latestAcceptance.primary_contact_phone || '';
                }

                if (!pcFirstName && !pcLastName) {
                    pcFirstName = leadData.first_name || orgData.primary_contact_first_name || '';
                    pcLastName = leadData.last_name || orgData.primary_contact_last_name || '';
                    if (!pcFirstName && !pcLastName) {
                        const fullNameFallback = orgData.primary_contact_name || leadData.contact_name || '';
                        if (fullNameFallback) {
                            const parsed = splitFullName(fullNameFallback);
                            pcFirstName = parsed.firstName;
                            pcLastName = parsed.lastName;
                        }
                    }
                }
                if (!pcEmail) {
                    pcEmail = leadData.email || orgData.primary_contact_email || '';
                }
                if (!pcPhone) {
                    pcPhone = leadData.phone_number || orgData.phone_number || '';
                }

                const pcCombinedName = [pcFirstName, pcLastName].filter(Boolean).join(' ');

                this.primaryContact = {
                    firstName: pcFirstName,
                    lastName: pcLastName,
                    first_name: pcFirstName,
                    last_name: pcLastName,
                    name: pcCombinedName,
                    email: pcEmail,
                    phone: pcPhone,
                    isSigner: true 
                };

                let signersToLoad = [];
                const seenEmails = new Set();
                if (this.contractAcceptances && this.contractAcceptances.length > 0 && this.contractAcceptances[0].signers && this.contractAcceptances[0].signers.length > 0) {
                    // Use signers from the latest acceptance (index 0, ordered by accepted_at DESC)
                    this.contractAcceptances[0].signers.forEach((s, idx) => {
                        const emailLower = s.email ? s.email.toLowerCase().trim() : '';
                        if (emailLower && seenEmails.has(emailLower)) {
                            return; // Skip duplicate signers loaded from previous acceptance records
                        }
                        if (emailLower) {
                            seenEmails.add(emailLower);
                        }
                        const primaryEmailLower = this.primaryContact.email ? this.primaryContact.email.toLowerCase().trim() : '';
                        const isPrimary = emailLower === primaryEmailLower || (primaryEmailLower === '' && idx === 0 && this.primaryContact.isSigner);

                        let sFirst = s.first_name || s.firstName || '';
                        let sLast = s.last_name || s.lastName || '';
                        if (!sFirst && !sLast && s.name) {
                            const parsed = splitFullName(s.name);
                            sFirst = parsed.firstName;
                            sLast = parsed.lastName;
                        }
                        const sName = [sFirst, sLast].filter(Boolean).join(' ') || s.name || '';

                        signersToLoad.push({
                            firstName: sFirst,
                            lastName: sLast,
                            first_name: sFirst,
                            last_name: sLast,
                            name: sName,
                            email: s.email,
                            role: s.role,
                            isPrimaryContact: isPrimary
                        });
                    });
                } else {
                    if (this.primaryContact.isSigner) {
                        const primaryEmail = this.primaryContact.email ? this.primaryContact.email.toLowerCase().trim() : '';
                        if (primaryEmail) seenEmails.add(primaryEmail);
                        signersToLoad.push({ 
                            firstName: this.primaryContact.firstName,
                            lastName: this.primaryContact.lastName,
                            first_name: this.primaryContact.firstName,
                            last_name: this.primaryContact.lastName,
                            name: this.primaryContact.name, 
                            email: this.primaryContact.email, 
                            role: 'Primary Signatory',
                            isPrimaryContact: true
                        });
                    }
                }

                // Add any signers from sharedUsers that are not already in the list
                if (this.sharedUsers && this.sharedUsers.length > 0) {
                    this.sharedUsers.forEach(user => {
                        if (user.share_type === 'signer' && !user.is_access_revoked) {
                            const emailLower = user.email ? user.email.toLowerCase().trim() : '';
                            if (emailLower && !seenEmails.has(emailLower)) {
                                // Check if this email matches primary contact email (if primary contact is a signer)
                                const matchesPrimary = this.primaryContact.isSigner && this.primaryContact.email && this.primaryContact.email.toLowerCase().trim() === emailLower;
                                if (!matchesPrimary) {
                                    seenEmails.add(emailLower);
                                    const assignedRole = signersToLoad.length === 0 ? 'Primary Signatory' : 'Signatory';
                                    let uFirst = user.first_name || '';
                                    let uLast = user.last_name || '';
                                    if (!uFirst && !uLast && user.name) {
                                        const parsed = splitFullName(user.name);
                                        uFirst = parsed.firstName;
                                        uLast = parsed.lastName;
                                    }
                                    const uName = [uFirst, uLast].filter(Boolean).join(' ') || user.name || '';
                                    signersToLoad.push({
                                        firstName: uFirst,
                                        lastName: uLast,
                                        first_name: uFirst,
                                        last_name: uLast,
                                        name: uName,
                                        email: user.email,
                                        role: assignedRole,
                                        isPrimaryContact: false
                                    });
                                }
                            }
                        }
                    });
                }

                if (signersToLoad.length === 0) {
                    signersToLoad.push({
                        firstName: '',
                        lastName: '',
                        first_name: '',
                        last_name: '',
                        name: '',
                        email: '',
                        role: 'Primary Signatory',
                        isPrimaryContact: false
                    });
                }
                
                this.signers = signersToLoad;

                if (this.contractDetails.abn) {
                    this.lookupABN();
                }
            }
        },

        'primaryContact.isSigner'(newVal) {
            if (newVal) {
                const primaryEmailLower = (this.primaryContact.email || '').trim().toLowerCase();
                let pSigner = this.signers.find(s => s.isPrimaryContact);
                if (!pSigner) {
                    // Remove any existing non-primary entry that had this same email to avoid instant duplicate collision
                    if (primaryEmailLower) {
                        const dupIdx = this.signers.findIndex(s => !s.isPrimaryContact && s.email && s.email.trim().toLowerCase() === primaryEmailLower);
                        if (dupIdx !== -1) {
                            this.signers.splice(dupIdx, 1);
                        }
                    }
                    this.signers.unshift({
                        firstName: this.primaryContact.firstName,
                        lastName: this.primaryContact.lastName,
                        first_name: this.primaryContact.firstName,
                        last_name: this.primaryContact.lastName,
                        name: this.primaryContact.name,
                        email: this.primaryContact.email,
                        role: 'Primary Signatory',
                        isPrimaryContact: true
                    });
                } else {
                    pSigner.firstName = this.primaryContact.firstName;
                    pSigner.lastName = this.primaryContact.lastName;
                    pSigner.first_name = this.primaryContact.firstName;
                    pSigner.last_name = this.primaryContact.lastName;
                    pSigner.name = this.primaryContact.name;
                    pSigner.email = this.primaryContact.email;
                }
            } else {
                const idx = this.signers.findIndex(s => s.isPrimaryContact);
                if (idx !== -1) {
                    this.signers.splice(idx, 1);
                }
                // Ensure we have at least 1 signer remaining
                if (this.signers.length === 0) {
                    this.signers.push({
                        firstName: '',
                        lastName: '',
                        first_name: '',
                        last_name: '',
                        name: '',
                        email: '',
                        role: 'Primary Signatory',
                        isPrimaryContact: false
                    });
                }
            }
            this.updateSignerRoles();
        },
        'primaryContact.firstName'() {
            this.syncPrimaryContactName();
        },
        'primaryContact.lastName'() {
            this.syncPrimaryContactName();
        },
        'primaryContact.name'(newVal) {
            const pSigner = this.signers.find(s => s.isPrimaryContact);
            if (pSigner) {
                pSigner.name = newVal;
            }
        },
        'primaryContact.email'(newVal) {
            const pSigner = this.signers.find(s => s.isPrimaryContact);
            if (pSigner) {
                pSigner.email = newVal;
            }
        },
        contractCountryCode() {
            if (this.contractDetails.abn?.trim()) {
                this.lookupABN();
            } else {
                this.abnLookup = { isLoading: false, status: 'idle', message: '' };
                this.abnRecordFound = false;
            }
        },
        isAustraliaResident() {
            if (this.contractDetails.abn?.trim()) {
                this.lookupABN();
            } else {
                this.abnLookup = { isLoading: false, status: 'idle', message: '' };
                this.abnRecordFound = false;
            }
        }
    },
    computed: {
        allToSign() {
            return this.signers.length > 1;
        },
        contractCountryCode() {
            const raw = this.contractDetails.country?.trim()
                || this.lead?.organisation?.country?.trim()
                || this.lead?.organisation?.country_code?.trim()
                || this.lead?.Organisation?.country?.trim()
                || this.lead?.Organisation?.country_code?.trim()
                || this.lead?.country_code?.trim()
                || 'AU';
            return normalizeCountryCode(raw);
        },
        businessNumberLabel() {
            return getBusinessNumberLabel(this.contractCountryCode);
        },
        businessNumberShortLabel() {
            return getBusinessNumberShortLabel(this.contractCountryCode);
        },
        businessNumberPlaceholder() {
            return getBusinessNumberPlaceholder(this.contractCountryCode);
        },
        isAustraliaResident() {
            const isOrgAU = isAustralianCountry(this.contractCountryCode);
            const serviceCountry = this.contractDetails.serviceCountry?.trim();
            const deliveryCountry = this.contractDetails.deliveryCountry?.trim();

            const isServiceAU = !this.contractDetails.isSameAsOrgAddress && Boolean(serviceCountry) && isAustralianCountry(serviceCountry);
            const isDeliveryAU = !this.contractDetails.isDeliverySameAsOrgAddress && Boolean(deliveryCountry) && isAustralianCountry(deliveryCountry);

            return isOrgAU || isServiceAU || isDeliveryAU;
        },
        organisationNameOptions() {
            if (!this.abnRecordFound || !this.abnDetails) return [];
            const names = [this.abnDetails.entity_name];
            if (this.abnDetails.business_names && this.abnDetails.business_names.length > 0) {
                const businessNames = this.abnDetails.business_names.map(bn => bn.name).filter(Boolean);
                names.push(...businessNames);
            }
            return [...new Set(names)];
        },
        isPrimarySignerValid() {
            const primarySignersCount = this.signers.filter(signer => signer.role === 'Primary Signatory').length;
            return primarySignersCount === 1;
        },
        hasDuplicateSignerEmails() {
            const emails = this.signers
                .map(s => (s.email || '').trim().toLowerCase())
                .filter(email => email.length > 0);
            return new Set(emails).size !== emails.length;
        },
        isSignerDetailsValid() {
            if (this.signers.some(signer => 
                !(signer.firstName || signer.first_name || '').trim() || 
                !(signer.lastName || signer.last_name || '').trim()
            )) return false;
            if (this.signers.some(signer => !this.isValidEmail(signer.email))) return false;
            if (this.hasDuplicateSignerEmails) return false;
            return true;
        },
        isStep3Valid() {
            const hasFirst = !!(this.primaryContact.firstName || this.primaryContact.first_name || '').trim();
            const hasLast = !!(this.primaryContact.lastName || this.primaryContact.last_name || '').trim();
            const contactValid = hasFirst && hasLast && this.isValidEmail(this.primaryContact.email) && this.primaryContact.phone && this.isContactPhoneValid;
            return contactValid && this.isPrimarySignerValid && this.isSignerDetailsValid;
        },
        currentOptionProducts() {
            const selectedOption = this.proposalItems.find(pi => pi.option_index === this.selectedContractOptionIndex);
            return selectedOption ? selectedOption.productList : [];
        },
        previouslyAcceptedItems() {
            return this.currentOptionProducts.filter(item => item.added_to_contract);
        },
        availableItems() {
            return this.currentOptionProducts.filter(item => !item.added_to_contract);
        },
        selectAllComputed: {
            get() {
                const availableSelectable = this.availableItems.filter(i => !i.is_heading && !i.IsHeading && (!i.isChild || parseFloat(i.price) > 0));
                if (availableSelectable.length === 0) return false;
                
                const selectedSelectableCount = this.selectedContractItems.filter(id => {
                    const item = this.availableItems.find(i => i.id === id);
                    return item && !item.is_heading && !item.IsHeading && (!item.isChild || parseFloat(item.price) > 0);
                }).length;
                
                return selectedSelectableCount === availableSelectable.length;
            },
            set(value) {
                const availableSelectable = this.availableItems.filter(i => !i.is_heading && !i.IsHeading && (!i.isChild || parseFloat(i.price) > 0));
                this.selectedContractItems = value ? availableSelectable.map(item => item.id) : [];
            }
        },
        selectedItemsSummary() {
            if (!this.selectedContractItems.length) return { subtotal: 0, gst: 0, total: 0 };
            const selectedProducts = this.currentOptionProducts.filter(p => this.selectedContractItems.includes(p.id));
            const subtotal = this.calculateOptionSubTotalPrice(selectedProducts);
            const gst = this.calculateOptionGST(selectedProducts);
            const total = this.calculateOptionTotalPrice(selectedProducts);
            return { subtotal, gst, total };
        },
        isStep2Valid() {
            const details = this.contractDetails;
            if (!details.organisationName?.trim() || !details.phoneNumber?.trim() || !details.serviceEmail?.trim() || !details.accountsEmail?.trim()) {
                return false;
            }
            if (!this.isPhoneValid) return false;

            if (this.isAustraliaResident) {
                if (!details.abn?.trim()) return false;
                if (this.abnLookup.status === 'error') return false;
            } else if (details.abn?.trim()) {
                if (this.abnLookup.status === 'error') return false;
            }

            if (this.manualAddress) {
                if (!details.address1?.trim() || !details.city?.trim() || !details.state?.trim() || !details.country?.trim() || !details.postCode?.trim()) return false;
            } else {
                 if (!details.address?.trim()) return false;
            }

            if (!details.isSameAsOrgAddress) {
                 if (this.manualServiceAddress) {
                    if (!details.serviceAddress1?.trim() || !details.serviceCity?.trim() || !details.serviceState?.trim() || !details.serviceCountry?.trim() || !details.servicePostCode?.trim()) return false;
                } else {
                    if (!details.serviceAddress?.trim()) return false;
                }
            }

            if (!details.isDeliverySameAsOrgAddress) {
                 if (this.manualDeliveryAddress) {
                    if (!details.deliveryAddress1?.trim() || !details.deliveryCity?.trim() || !details.deliveryState?.trim() || !details.deliveryCountry?.trim() || !details.deliveryPostCode?.trim()) return false;
                } else {
                    if (!details.deliveryAddress?.trim()) return false;
                }
            }

            if (!details.isBillingSameAsOrgAddress) {
                 if (this.manualBillingAddress) {
                    if (!details.billingAddress1?.trim() || !details.billingCity?.trim() || !details.billingState?.trim() || !details.billingCountry?.trim() || !details.billingPostCode?.trim()) return false;
                } else {
                    if (!details.billingAddress?.trim()) return false;
                }
            }

            if (!this.isValidEmail(details.serviceEmail)) return false;
            if (!this.isValidEmail(details.accountsEmail)) return false;

            return true;
        },
        subtotalTooltipText() {
            if (this.selectedContractItems.length === 0) {
                return 'No items selected.';
            }

            const selectedProducts = this.currentOptionProducts.filter(p =>
                this.selectedContractItems.includes(p.id) && (!p.isChild || parseFloat(p.price) > 0)
            );
            const header = "Subtotal Calculation:\n";
            const breakdown = selectedProducts.map(p => {
                const qty = p.quantity || 0;
                const price = p.price || 0;
                const discount = p.discount || 0;
                const finalUnitPrice = price - discount;
                const lineTotal = finalUnitPrice * qty;
                let line = `${p.product_name || p.Product?.display_name || p.Product?.name || 'Unknown Product'}: `;
                if (discount > 0) {
                    line += `${qty} x (${this.formatCurrency(price)} - ${this.formatCurrency(discount)}) = ${this.formatCurrency(lineTotal)}`;
                } else {
                    line += `${qty} x ${this.formatCurrency(price)} = ${this.formatCurrency(lineTotal)}`;
                }
                return line;
            });
            return [header, ...breakdown].join('\n');
        },
    },
    methods: {
        toOptionName,
        close() { this.$emit('close'); },
        nextStep() { if (this.currentStep < 4) this.currentStep++; },
        prevStep() { if (this.currentStep > 1) this.currentStep--; },
        handleOptionChange() { this.$nextTick(() => { this.selectAllComputed = true; }); },
        addSigner() {
            const assignedRole = this.signers.length === 0 ? 'Primary Signatory' : 'Signatory';
            this.signers.push({ 
                firstName: '', 
                lastName: '', 
                first_name: '', 
                last_name: '', 
                name: '', 
                email: '', 
                role: assignedRole, 
                isPrimaryContact: false 
            });
        },
        syncPrimaryContactName() {
            this.primaryContact.first_name = this.primaryContact.firstName;
            this.primaryContact.last_name = this.primaryContact.lastName;
            const combined = [this.primaryContact.firstName, this.primaryContact.lastName].map(s => (s || '').trim()).filter(Boolean).join(' ');
            this.primaryContact.name = combined;
            if (this.primaryContact.isSigner) {
                const pSigner = this.signers.find(s => s.isPrimaryContact);
                if (pSigner) {
                    pSigner.firstName = this.primaryContact.firstName;
                    pSigner.lastName = this.primaryContact.lastName;
                    pSigner.first_name = this.primaryContact.firstName;
                    pSigner.last_name = this.primaryContact.lastName;
                    pSigner.name = combined;
                }
            }
        },
        syncSignerName(signer) {
            if (!signer) return;
            signer.first_name = signer.firstName;
            signer.last_name = signer.lastName;
            signer.name = [signer.firstName, signer.lastName].map(s => (s || '').trim()).filter(Boolean).join(' ');
        },
        removeSigner(index) {
            if (this.signers.length > 1) {
                if (this.signers[index].isPrimaryContact) {
                    this.primaryContact.isSigner = false;
                } else {
                    this.signers.splice(index, 1);
                    this.updateSignerRoles();
                }
            }
        },
        updateSignerRoles() {
            this.signers.forEach((s, idx) => {
                s.role = idx === 0 ? 'Primary Signatory' : 'Signatory';
            });
        },
        isDuplicateSignerEmail(email, index) {
            if (!email || !email.trim()) return false;
            const target = email.trim().toLowerCase();
            return this.signers.some((s, idx) => idx !== index && s.email && s.email.trim().toLowerCase() === target);
        },

        moveSignerUp(index) {
            if (index > 0) {
                const temp = this.signers[index];
                this.signers.splice(index, 1);
                this.signers.splice(index - 1, 0, temp);
                this.updateSignerRoles();
            }
        },
        moveSignerDown(index) {
            if (index < this.signers.length - 1) {
                const temp = this.signers[index];
                this.signers.splice(index, 1);
                this.signers.splice(index + 1, 0, temp);
                this.updateSignerRoles();
            }
        },
        handleAbnInput() {
            clearTimeout(this.abnDebounceTimer);
            if (!this.contractDetails.abn || this.contractDetails.abn.trim() === '') {
                this.abnRecordFound = false;
                this.abnLookup = { isLoading: false, status: 'idle', message: '' };
                return;
            }
            this.abnDebounceTimer = setTimeout(() => { this.lookupABN(); }, 1000);
        },
        async lookupABN() {
            if (!this.contractDetails.abn || this.contractDetails.abn.trim() === '') {
                this.abnRecordFound = false;
                this.abnLookup = { isLoading: false, status: 'idle', message: '' };
                this.abnDetails = null;
                return;
            }

            if (!this.isAustraliaResident) {
                const isValid = validateBusinessNumber(this.contractDetails.abn, this.contractCountryCode);
                if (isValid) {
                    this.abnLookup = { isLoading: false, status: 'success', message: `${this.businessNumberShortLabel} format valid.` };
                } else {
                    this.abnLookup = { isLoading: false, status: 'error', message: `Invalid ${this.businessNumberShortLabel} format.` };
                }
                this.abnRecordFound = false;
                this.abnDetails = null;
                return;
            }

            const abn = this.contractDetails.abn.replace(/\s/g, '');
            if (!abn || (abn.length !== 11 && abn.length !== 9)) { 
                this.abnLookup = { isLoading: false, status: 'error', message: abn.length > 0 ? 'ABN must be 11 digits (or 9 digits for ACN).' : '' };
                this.abnRecordFound = false;
                this.abnDetails = null;
                return; 
            }
            this.abnLookup.isLoading = true;
            try {
                const resp = await this.$axios_internal.post('/abn/look-up', {
                    abn: abn,
                    entityName: this.contractDetails.organisationName,
                });
                this.abnDetails = resp.data.abn_details;
                this.abnRecordFound = true;
                
                // Guard to keep/choose existing organisationName if it exists and is correct/valid for this ABN
                const names = [this.abnDetails.entity_name];
                if (this.abnDetails.business_names && this.abnDetails.business_names.length > 0) {
                    const businessNames = this.abnDetails.business_names.map(bn => bn.name).filter(Boolean);
                    names.push(...businessNames);
                }
                const uniqueNames = [...new Set(names)];

                const currentName = (this.contractDetails.organisationName || '').trim().toLowerCase();
                const matchedName = uniqueNames.find(name => (name || '').trim().toLowerCase() === currentName);

                if (matchedName) {
                    this.contractDetails.organisationName = matchedName;
                } else {
                    this.contractDetails.organisationName = this.abnDetails.entity_name;
                }

                this.abnLookup.status = 'success';
                this.abnLookup.message = '';
            } catch (error) {
                this.abnRecordFound = false;
                this.abnLookup.status = 'error';
                this.abnLookup.message = error.response?.data?.message || 'Mismatch or Invalid ABN';
            } finally {
                this.abnLookup.isLoading = false;
            }
        },
        parseAddressComponents(place) {
            if (!place || !place.address_components) return { address1: '', address2: '', city: '', state: '', country: '', postCode: '' };
            let components = {};
            place.address_components.forEach(c => { c.types.forEach(type => { components[type] = c; }); });
            const streetNumber = components.street_number ? components.street_number.long_name : '';
            const route = components.route ? components.route.long_name : '';
            const address1 = `${streetNumber} ${route}`.trim();
            let address2 = components.subpremise ? `Unit ${components.subpremise.long_name}` : (components.level ? `Level ${components.level.long_name}` : '');
            let city = components.locality ? components.locality.long_name : (components.postal_town ? components.postal_town.long_name : (components.sublocality ? components.sublocality.long_name : ''));
            const state = components.administrative_area_level_1 ? (components.administrative_area_level_1.short_name || components.administrative_area_level_1.long_name) : '';
            const country = components.country ? components.country.long_name : '';
            const postCode = components.postal_code ? components.postal_code.long_name : '';
            return { address1, address2, city, state, country, postCode };
        },
        handleAddressSelected(place) {
            if (!place) return;
            if (place.formatted_address) this.contractDetails.address = place.formatted_address;
            const parsed = this.parseAddressComponents(place);
            Object.assign(this.contractDetails, { 
                address1: parsed.address1, address2: parsed.address2, city: parsed.city, 
                state: parsed.state, country: parsed.country, postCode: parsed.postCode 
            });
        },
        handleServiceAddressSelected(place) {
            if (!place) return;
            if (place.formatted_address) this.contractDetails.serviceAddress = place.formatted_address;
            const parsed = this.parseAddressComponents(place);
            Object.assign(this.contractDetails, { 
                serviceAddress1: parsed.address1, serviceAddress2: parsed.address2, serviceCity: parsed.city, 
                serviceState: parsed.state, serviceCountry: parsed.country, servicePostCode: parsed.postCode 
            });
        },
        handleDeliveryAddressSelected(place) {
            if (!place) return;
            if (place.formatted_address) this.contractDetails.deliveryAddress = place.formatted_address;
            const parsed = this.parseAddressComponents(place);
            Object.assign(this.contractDetails, { 
                deliveryAddress1: parsed.address1, deliveryAddress2: parsed.address2, deliveryCity: parsed.city, 
                deliveryState: parsed.state, deliveryCountry: parsed.country, deliveryPostCode: parsed.postCode 
            });
        },
        handleBillingAddressSelected(place) {
            if (!place) return;
            if (place.formatted_address) this.contractDetails.billingAddress = place.formatted_address;
            const parsed = this.parseAddressComponents(place);
            Object.assign(this.contractDetails, { 
                billingAddress1: parsed.address1, billingAddress2: parsed.address2, billingCity: parsed.city, 
                billingState: parsed.state, billingCountry: parsed.country, billingPostCode: parsed.postCode 
            });
        },
        updateFullAddress() {
            const parts = [this.contractDetails.address1, this.contractDetails.address2, this.contractDetails.city, this.contractDetails.state, this.contractDetails.country, this.contractDetails.postCode];
            this.contractDetails.address = parts.filter(p => p && p.trim()).join(', ');
        },
        updateFullServiceAddress() {
            const parts = [this.contractDetails.serviceAddress1, this.contractDetails.serviceAddress2, this.contractDetails.serviceCity, this.contractDetails.serviceState, this.contractDetails.serviceCountry, this.contractDetails.servicePostCode];
            this.contractDetails.serviceAddress = parts.filter(p => p && p.trim()).join(', ');
        },
        updateFullDeliveryAddress() {
            const parts = [this.contractDetails.deliveryAddress1, this.contractDetails.deliveryAddress2, this.contractDetails.deliveryCity, this.contractDetails.deliveryState, this.contractDetails.deliveryCountry, this.contractDetails.deliveryPostCode];
            this.contractDetails.deliveryAddress = parts.filter(p => p && p.trim()).join(', ');
        },
        updateFullBillingAddress() {
            const parts = [this.contractDetails.billingAddress1, this.contractDetails.billingAddress2, this.contractDetails.billingCity, this.contractDetails.billingState, this.contractDetails.billingCountry, this.contractDetails.billingPostCode];
            this.contractDetails.billingAddress = parts.filter(p => p && p.trim()).join(', ');
        },
    async handleSubmitContract() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;
            const selectedOption = this.proposalItems.find(pi => pi.option_index == this.selectedContractOptionIndex);
            const selectedOptionPrice = selectedOption ? this.calculateOptionTotalPrice(selectedOption.productList) : 0;

            let itemsToSubmit = [];
            let lastParentId = null;

            this.currentOptionProducts.forEach(item => {
                if (item.is_heading || item.IsHeading) {
                    if (!item.added_to_contract) {
                        itemsToSubmit.push(item.id);
                    }
                    return;
                }
                if (!item.isChild || parseFloat(item.price) > 0) {
                    if (!item.isChild) {
                        lastParentId = item.id;
                    }
                    if (this.selectedContractItems.includes(item.id)) {
                        itemsToSubmit.push(item.id);
                    }
                } else {
                    if (lastParentId && this.selectedContractItems.includes(lastParentId)) {
                        if (!item.added_to_contract) {
                            itemsToSubmit.push(item.id);
                        }
                    }
                }
            });

            let quantitiesMap = {};
            this.currentOptionProducts.forEach(item => {
                if (!item.is_heading && !item.IsHeading && (!item.isChild || parseFloat(item.price) > 0)) {
                    quantitiesMap[item.id] = parseInt(item.quantity) || 1;
                }
            });

            const finalServiceAddress = this.contractDetails.isSameAsOrgAddress ? this.contractDetails.address : this.contractDetails.serviceAddress;
            const finalServiceAddress1 = this.contractDetails.isSameAsOrgAddress ? (this.contractDetails.address1 || '') : (this.contractDetails.serviceAddress1 || '');
            const finalServiceAddress2 = this.contractDetails.isSameAsOrgAddress ? (this.contractDetails.address2 || '') : (this.contractDetails.serviceAddress2 || '');
            const finalServiceCity = this.contractDetails.isSameAsOrgAddress ? (this.contractDetails.city || '') : (this.contractDetails.serviceCity || '');
            const finalServiceState = this.contractDetails.isSameAsOrgAddress ? (this.contractDetails.state || '') : (this.contractDetails.serviceState || '');
            const finalServiceCountry = this.contractDetails.isSameAsOrgAddress ? (this.contractDetails.country || '') : (this.contractDetails.serviceCountry || '');
            const finalServicePostCode = this.contractDetails.isSameAsOrgAddress ? (this.contractDetails.postCode || '') : (this.contractDetails.servicePostCode || '');

            const finalBillingAddress = this.contractDetails.isBillingSameAsOrgAddress ? this.contractDetails.address : this.contractDetails.billingAddress;
            const finalBillingAddress1 = this.contractDetails.isBillingSameAsOrgAddress ? (this.contractDetails.address1 || '') : (this.contractDetails.billingAddress1 || '');
            const finalBillingAddress2 = this.contractDetails.isBillingSameAsOrgAddress ? (this.contractDetails.address2 || '') : (this.contractDetails.billingAddress2 || '');
            const finalBillingCity = this.contractDetails.isBillingSameAsOrgAddress ? (this.contractDetails.city || '') : (this.contractDetails.billingCity || '');
            const finalBillingState = this.contractDetails.isBillingSameAsOrgAddress ? (this.contractDetails.state || '') : (this.contractDetails.billingState || '');
            const finalBillingCountry = this.contractDetails.isBillingSameAsOrgAddress ? (this.contractDetails.country || '') : (this.contractDetails.billingCountry || '');
            const finalBillingPostCode = this.contractDetails.isBillingSameAsOrgAddress ? (this.contractDetails.postCode || '') : (this.contractDetails.billingPostCode || '');

            const detailsPayload = {
                organisationName: this.contractDetails.organisationName,
                abn: this.contractDetails.abn,
                phoneNumber: this.contractDetails.phoneNumber, 
                address: this.contractDetails.address, 
                address_1: this.contractDetails.address1 || '',
                address_2: this.contractDetails.address2 || '',
                city: this.contractDetails.city || '',
                state: this.contractDetails.state || '',
                country: this.contractDetails.country || '',
                post_code: this.contractDetails.postCode || '',
                isSameAsOrgAddress: this.contractDetails.isSameAsOrgAddress,
                serviceAddress: finalServiceAddress,
                service_address_1: finalServiceAddress1,
                service_address_2: finalServiceAddress2,
                service_city: finalServiceCity,
                service_state: finalServiceState,
                service_country: finalServiceCountry,
                service_post_code: finalServicePostCode,
                
                isDeliverySameAsOrgAddress: this.contractDetails.isDeliverySameAsOrgAddress,
                deliveryAddress: this.contractDetails.isDeliverySameAsOrgAddress ? this.contractDetails.address : this.contractDetails.deliveryAddress,
                delivery_address_1: this.contractDetails.isDeliverySameAsOrgAddress ? (this.contractDetails.address1 || '') : (this.contractDetails.deliveryAddress1 || ''),
                delivery_address_2: this.contractDetails.isDeliverySameAsOrgAddress ? (this.contractDetails.address2 || '') : (this.contractDetails.deliveryAddress2 || ''),
                delivery_city: this.contractDetails.isDeliverySameAsOrgAddress ? (this.contractDetails.city || '') : (this.contractDetails.deliveryCity || ''),
                delivery_state: this.contractDetails.isDeliverySameAsOrgAddress ? (this.contractDetails.state || '') : (this.contractDetails.deliveryState || ''),
                delivery_country: this.contractDetails.isDeliverySameAsOrgAddress ? (this.contractDetails.country || '') : (this.contractDetails.deliveryCountry || ''),
                delivery_post_code: this.contractDetails.isDeliverySameAsOrgAddress ? (this.contractDetails.postCode || '') : (this.contractDetails.deliveryPostCode || ''),

                isBillingSameAsOrgAddress: this.contractDetails.isBillingSameAsOrgAddress,
                billingAddress: finalBillingAddress,
                billing_address_1: finalBillingAddress1,
                billing_address_2: finalBillingAddress2,
                billing_city: finalBillingCity,
                billing_state: finalBillingState,
                billing_country: finalBillingCountry,
                billing_post_code: finalBillingPostCode,

                serviceEmail: this.contractDetails.serviceEmail,
                accountsEmail: this.contractDetails.accountsEmail,

                primary_contact_first_name: (this.primaryContact.firstName || this.primaryContact.first_name || '').trim(),
                primary_contact_last_name: (this.primaryContact.lastName || this.primaryContact.last_name || '').trim(),
                primary_contact_name: this.primaryContact.name,
                primary_contact_email: this.primaryContact.email,
                primary_contact_phone: this.primaryContact.phone,
                primary_contact_is_signer: this.primaryContact.isSigner
            };

            const mappedSigners = this.signers.map((s, idx) => {
                const firstName = (s.firstName || s.first_name || '').trim();
                const lastName = (s.lastName || s.last_name || '').trim();
                const name = [firstName, lastName].filter(Boolean).join(' ') || (s.name || '').trim();
                return {
                    first_name: firstName,
                    last_name: lastName,
                    name: name,
                    email: s.email,
                    role: s.role,
                    sequence: idx + 1
                };
            });

            try {
                const response = await this.$axios_internal.post(`/proposals/${this.proposalUniqueKey}/accept/${this.proposalAccessKey}`, {
                    isNotMainOption: this.selectedContractOptionIndex != 1,
                    newTotalAmount: selectedOptionPrice,
                    selectedOption: this.selectedContractOptionIndex,
                    selectedItems: itemsToSubmit, 
                    details: detailsPayload,
                    signers: mappedSigners,
                    quantities: quantitiesMap,
                    allToSign: this.allToSign,
                });
                this.$toast.success('Contract submitted successfully!');
                this.$emit('acceptance-success', response.data.proposal);
                
                if (response.data.redirect_to_contract && response.data.contract_unique_key && response.data.shared_access_key) {
                    this.$router.push({ 
                        name: 'clientContract', 
                        params: { 
                            unique_key: response.data.contract_unique_key, 
                            access_key: response.data.shared_access_key 
                        } 
                    });
                } else {
                    this.$router.push({ name: 'ProposalThankYou' });
                }
                this.close();
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'An unexpected error occurred.');
            } finally {
                this.isSubmitting = false;
            }
        },
        calculateOptionTotalPrice(productList) { return productList.filter(p => !p.is_heading && !p.IsHeading && (!p.isChild || parseFloat(p.price) > 0)).reduce((total, p) => total + (((p.price || 0) - (p.discount || 0)) * (p.quantity || 0)), 0) * 1.1; },
        calculateOptionSubTotalPrice(productList) { return productList.filter(p => !p.is_heading && !p.IsHeading && (!p.isChild || parseFloat(p.price) > 0)).reduce((total, p) => total + (((p.price || 0) - (p.discount || 0)) * (p.quantity || 0)), 0); },
        calculateOptionGST(productList) { return this.calculateOptionSubTotalPrice(productList) * 0.1; },
        formatCurrency(value) {
            const numericValue = parseFloat(value);
            return isNaN(numericValue) ? '$0.00' : `$${numericValue.toFixed(2)}`;
        },
        isValidEmail(email) {
            const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(String(email).toLowerCase());
        },
        validateEmail(fieldName) {
            const email = this.contractDetails[fieldName];
            if (!email) { 
                this.emailErrors[fieldName] = 'This field is required.'; 
                return; 
            }
            this.emailErrors[fieldName] = !this.isValidEmail(email) ? 'Please enter a valid email address.' : '';
        },
        handlePhoneValidate(phoneObject) { this.isPhoneValid = phoneObject?.valid; },
        handleContactPhoneValidate(phoneObject) { this.isContactPhoneValid = phoneObject?.valid; },
        validateQty(item) {
            if (item.quantity === '' || item.quantity === null || isNaN(item.quantity) || item.quantity < 1) {
                item.quantity = 1;
            } else {
                item.quantity = Math.floor(item.quantity);
            }
        }
    }
}
</script>

<style scoped>
/* (All original styles preserved) */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}
.sample-contract-modal {
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}
.proceed-modal-custom {
    max-width: 750px;
}
.modal-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e9ecef;
    flex-shrink: 0;
}
.modal-title-custom {
    font-size: 1.25rem;
    font-weight: 500;
    margin: 0;
    color: #333;
}
.close-button-custom {
    background: none;
    border: none;
    font-size: 1.75rem;
    font-weight: bold;
    line-height: 1;
    color: #6c757d;
    cursor: pointer;
    opacity: 0.7;
}
.close-button-custom:hover {
    opacity: 1;
}
.modal-body-custom {
    padding: 1.5rem;
    overflow-y: auto;
    flex-grow: 1;
    background-color: #fbfbfb;
}
.modal-footer-custom {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 1rem 1.5rem;
    border-top: 1px solid #e9ecef;
    flex-shrink: 0;
    background-color: #f8f9fa;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
}
.step-indicator {
    display: flex;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e9ecef;
    background-color: #fff;
}
.step {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #adb5bd;
    position: relative;
}
.step-icon {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: #fff;
    border: 2px solid #e9ecef;
    display: grid;
    place-items: center;
    font-weight: bold;
    transition: all 0.3s;
    color: #adb5bd;
}
.step-label {
    font-size: 0.85rem;
    margin-top: 8px;
    white-space: nowrap;
    font-weight: 500;
    transition: color 0.3s;
}
.step.active .step-icon {
    border-color: #00a0c1;
    background-color: #00a0c1;
    color: white;
}
.step.active .step-label {
    color: #00a0c1;
}
.step.completed .step-icon {
    border-color: #00a0c1;
    background-color: #00a0c1;
    color: white;
}
.step.completed .step-label {
    color: #333;
}
.step-connector {
    flex-grow: 1;
    height: 2px;
    background-color: #e9ecef;
    margin: 0 10px;
    margin-bottom: 30px;
    transition: background-color 0.3s;
}
.step-connector.completed {
    background-color: #00a0c1;
}
.button {
    border: none;
    border-radius: 8px !important;
    padding: 12px 25px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
    min-width: 140px;
    text-align: center;
}
.button.primary {
    background-color: #00a0c1;
    color: white;
}
.button.primary:hover {
    background-color: #008da9;
}
.button.primary:disabled {
    background-color: #a0a0a0;
    cursor: not-allowed;
}
.button.secondary {
    background-color: #6c757d;
    color: white;
}
.button.secondary:hover {
    background-color: #5a6268;
}
.items-selection-wrapper .form-group {
    margin-bottom: 1.5rem;
}
.items-selection-wrapper p {
    margin-bottom: 0.75rem;
    font-weight: 500;
}
.item-list {
    max-height: 380px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
    background-color: #fff;
}
.item-row-disabled {
    background-color: #f8f9fa;
    opacity: 0.7;
}
.item-row-disabled label,
.item-row-disabled .item-description,
.item-row-disabled .item-price {
    color: #6c757d;
    cursor: default;
}
.item-status-tag {
    font-size: 0.75rem;
    font-weight: bold;
    color: #28a745;
    margin-left: 8px;
}
.item-list-separator {
    border: 0;
    border-top: 1px dashed #ced4da;
    margin: 10px 0;
}
.no-items-message {
    text-align: center;
    color: #6c757d;
    padding: 20px;
}
.item-row {
    display: flex;
    align-items: center;
    padding: 12px 5px;
}
.item-row:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
}
.item-selection {
    padding-right: 12px;
}
.stepper-pricing-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.95rem;
}
.stepper-pricing-table th,
.stepper-pricing-table td {
    padding: 12px 10px;
    vertical-align: top;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
}
.stepper-pricing-table th {
    font-weight: 600;
    color: #495057;
    background-color: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
    vertical-align: middle;
}
.stepper-pricing-table .col-select {
    width: 40px;
    text-align: center;
}
.stepper-pricing-table .col-product {
    /* takes remaining space, which is very wide now because right columns are narrow */
}
.stepper-pricing-table .col-qty {
    width: 60px;
    text-align: center;
    padding-left: 4px;
    padding-right: 4px;
}
.stepper-pricing-table .col-unit-price {
    width: 80px;
    text-align: right;
    padding-left: 4px;
    padding-right: 4px;
}
.stepper-pricing-table th.col-unit-price {
    text-align: right;
}
.stepper-pricing-table .col-discount {
    width: 100px;
    text-align: right;
    padding-left: 4px;
    padding-right: 4px;
}
.stepper-pricing-table th.col-discount {
    text-align: right;
}
.stepper-pricing-table .col-total {
    width: 100px;
    text-align: right;
    font-weight: 500;
    white-space: nowrap;
    padding-left: 4px;
    padding-right: 4px;
}
.stepper-pricing-table th.col-total {
    text-align: right;
}
.stepper-pricing-table th.col-qty {
    text-align: center;
}
.stepper-pricing-table .item-description {
    font-size: 0.9em;
    color: #444;
    line-height: 1.4;
    margin-top: 6px;
}
.stepper-pricing-table .item-description ul {
    margin-top: 0;
    margin-bottom: 8px;
    padding-left: 20px;
    list-style-type: disc !important;
}
.stepper-pricing-table .item-description li {
    margin-bottom: 4px;
}
.stepper-pricing-table .badge-included {
    background-color: #00a0c1;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 600;
    display: inline-block;
}
.stepper-pricing-table .qty-input-field {
    width: 48px;
    padding: 4px;
    border: 1px solid #ced4da;
    border-radius: 6px;
    text-align: center;
    font-size: 0.9rem;
    font-weight: 600;
    color: #495057;
    background-color: #fff;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.stepper-pricing-table .qty-input-field:focus {
    border-color: #00a0c1;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.25);
}
.stepper-pricing-table .qty-input-field:disabled {
    background-color: #e9ecef;
    border-color: #dee2e6;
    color: #6c757d;
    cursor: not-allowed;
}
.stepper-pricing-table .select-all-row {
    background-color: #f8f9fa;
}
.stepper-pricing-table .select-all-row label {
    margin-bottom: 0;
    cursor: pointer;
}
.stepper-pricing-table .child-item-row {
    background-color: #fafafa;
}
.stepper-pricing-table .child-product-title {
    color: #495057;
}
.stepper-pricing-table .item-row-disabled {
    opacity: 0.7;
    background-color: #f8f9fa;
}
.stepper-pricing-table input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #00a0c1;
}
.select-all-row {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
    margin: -1rem -1rem 0.5rem -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
}
.contract-summary {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #eee;
}
.contract-summary h4 {
    margin-bottom: 10px;
    font-size: 1.1em;
    color: #333;
}
.summary-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 0.95em;
}
.summary-row.total {
    font-size: 1.1em;
    border-top: 1px solid #ddd;
    margin-top: 5px;
    padding-top: 10px;
}
.contract-details-form,
.signers-form {
    display: flex;
    flex-direction: column;
    gap: 15px;
}
.form-description {
    color: #6c757d;
    font-size: 0.95rem;
    margin-top: 0;
    margin-bottom: 10px;
}
.form-group {
    display: flex;
    flex-direction: column;
}
.form-group label {
    margin-bottom: 8px;
    font-weight: 500;
    color: #333;
    font-size: 14px;
}
.form-control {
    width: 100%;
    padding: 10px;
    border-radius: 6px;
    border: 1px solid #ccc;
    font-size: 1rem;
    box-sizing: border-box;
}
.form-control:disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
}
.form-row {
    display: flex;
    gap: 20px;
}
.half-width {
    flex: 1;
}
hr {
    border: none;
    border-top: 1px solid #eee;
    margin: 10px 0;
}
.required-star {
    color: red;
    margin-left: 3px;
}
.checkbox-group {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 5px;
}
.checkbox-group input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #00a0c1;
}
.checkbox-group label {
    margin-bottom: 0;
    font-weight: normal;
    font-size: 1rem;
}
.lookup-message {
    margin-top: 5px;
    font-size: 0.8rem;
    height: 1em;
}
.lookup-message.success {
    color: #28a745;
}
.lookup-message.error {
    color: #dc3545;
}
.error-message {
    color: #dc3545;
    font-size: 0.875rem;
    text-align: right;
    margin-top: 10px;
}
.step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.step-header h5 {
    margin: 0;
}
.button-add-signer {
    background: none;
    border: none;
    border-radius: 0px;
    padding: 8px 12px;
    color: #00a0c1;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: background-color 0.2s, color 0.2s;
}
.button-add-signer:hover {
    background-color: #e6f6f9;
}
.plus-icon {
    font-size: 1.2rem;
    margin-right: 4px;
    font-weight: bold;
}
.signers-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.signer-entry {
    position: relative;
    background-color: #fff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 15px;
}
.remove-signer-btn {
    background: none;
    border: none;
    color: #adb5bd;
    font-size: 1.25rem;
    font-weight: 600;
    cursor: pointer;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    padding: 0;
    transition: color 0.15s ease;
}
.remove-signer-btn:hover {
    color: #dc3545;
}
@keyframes float-wobble {
    0%,
    100% {
        transform: translateY(0) rotate(0);
    }
    25% {
        transform: translateY(-8px) rotate(5deg);
    }
    50% {
        transform: translateY(0) rotate(0);
    }
    75% {
        transform: translateY(-8px) rotate(-5deg);
    }
}
.finish-step {
    text-align: center;
    padding: 2rem 1rem;
}
.finish-icon {
    font-size: 4.5rem;
    margin-bottom: 2rem;
    display: inline-block;
    animation: float-wobble 2.5s ease-in-out infinite;
}
.finish-step h3 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 0.5rem;
}
.finish-step p {
    color: #666;
    max-width: 400px;
    margin: 0 auto;
    line-height: 1.6;
}
.autocomplete-wrapper {
    position: relative;
}
.autocomplete-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: white;
    border: 1px solid #ccc;
    border-top: none;
    border-radius: 0 0 6px 6px;
    z-index: 1010;
    max-height: 200px;
    overflow-y: auto;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
.autocomplete-result-item {
    padding: 10px;
    cursor: pointer;
    font-size: 0.95rem;
}
.autocomplete-result-item:hover {
    background-color: #f0f0f0;
}
.manual-address-container {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 5px;
}
.input-error {
    border-color: #dc3545 !important;
}
.form-control-phone {
    border-radius: 6px;
    border: 1px solid #ccc;
    font-size: 1rem;
}
::v-deep .vue-tel-input {
    border-radius: 6px;
    border: 1px solid #ccc;
    box-shadow: none !important;
}
::v-deep .vue-tel-input:focus-within {
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 8px rgba(0, 188, 212, 0.6) !important;
    border-color: #00bcd4;
}
::v-deep .vti__dropdown {
    background-color: #f8f9fa;
    border-radius: 6px 0 0 6px;
    padding: 8px 12px;
}
::v-deep .vti__dropdown:hover {
    background-color: #e9ecef;
}
::v-deep .vti__selection {
    font-size: 15px;
}
::v-deep .vti__dropdown-list {
    border-radius: 6px;
    border: 1px solid #ccc;
}
::v-deep .vti__input {
    padding: 10px;
    background: transparent;
}

.section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #00a0c1;
    margin-bottom: 15px;
    border-left: 3px solid #00a0c1;
    padding-left: 10px;
}
.is-signer-toggle {
    background: #f0f9fb;
    padding: 12px;
    border-radius: 6px;
    margin-top: 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.radio-group {
    display: flex;
    gap: 15px;
}
.radio-group label {
    margin: 0;
    cursor: pointer;
}
.disabled-entry {
    background-color: #f8f9fa;
    border-style: dashed;
}
.disabled-entry input {
    background-color: #eee;
}

/* -------------------------------------
   UI Enhancements for Child Rows 
------------------------------------- */

.child-item-row {
    background-color: #f8fbfc !important;
    border-top: 1px dashed #eaeaea !important;
    border-left: 4px solid #00a0c1 !important;
}

.badge-included {
 font-size: 0.85em;
  background-color: #00a0c1; /* Vertex Blue Background */
  color: #ffffff;            /* White text for sharp contrast */
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1); /* Subtle shadow for depth */
}

.child-product-title {
    color: #555;
    font-size: 0.95em;
    font-weight: 500;
}

/* -------------------------------------
   Signing Requirement & Sequencing UI 
------------------------------------- */
.signing-requirement-options {
    display: flex;
    gap: 15px;
    margin-top: 10px;
}
@media (max-width: 768px) {
    .signing-requirement-options {
        flex-direction: column;
    }
}
.signing-option-card {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 15px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex: 1;
    background: #ffffff;
}
.signing-option-card:hover {
    border-color: #00a0c1;
    background-color: #fcfdfe;
}
.selected-card {
    border-color: #00a0c1 !important;
    background-color: #f5fcfd !important;
    box-shadow: 0 0 0 1px #00a0c1;
}
.option-radio {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #ccc;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.selected-card .option-radio {
    border-color: #00a0c1;
}
.radio-inner {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #00a0c1;
}
.option-details {
    display: flex;
    flex-direction: column;
}
.option-title {
    font-weight: 600;
    font-size: 0.95rem;
    color: #333;
}
.option-desc {
    font-size: 0.8rem;
    color: #666;
    margin-top: 2px;
    line-height: 1.3;
}
.signer-entry-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    border-bottom: 1px dashed #eaeaea;
    padding-bottom: 8px;
}
.signer-sequence-info {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.signer-role-badge {
    font-size: 0.7rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.primary-badge {
    background-color: #00a0c1;
    color: #ffffff;
}
.regular-badge {
    background-color: #e9ecef;
    color: #495057;
}
.sequence-number-label {
    font-size: 0.8rem;
    color: #495057;
}
.linked-badge-text {
    font-size: 0.8rem;
    color: #00a0c1;
    font-weight: 600;
}
.signer-actions-box {
    display: flex;
    align-items: center;
    gap: 10px;
}
.reorder-controls {
    display: flex;
    gap: 4px;
}
.reorder-btn {
    background: #ffffff;
    border: 1px solid #ced4da;
    cursor: pointer;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    padding: 0;
    color: #495057;
    transition: all 0.15s ease;
}
.reorder-btn.up-btn {
    border-color: #00a0c1;
    color: #00a0c1;
}
.reorder-btn.up-btn:hover:not(:disabled) {
    background: #00a0c1;
    color: #ffffff;
    border-color: #00a0c1;
}
.reorder-btn.down-btn {
    border-color: #f3994d;
    color: #f3994d;
}
.reorder-btn.down-btn:hover:not(:disabled) {
    background: #f3994d;
    color: #ffffff;
    border-color: #f3994d;
}
.reorder-btn:disabled {
    opacity: 0.3;
    border-color: #ced4da;
    color: #ced4da;
    cursor: not-allowed;
}
</style>